import { StateCreator, create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { BulkImportJobStatusEnum } from '../models/bulk-import.model';

export type BulkProcessType =
  | 'product-import'
  | 'customer-import'
  | 'provider-import'
  | 'invoice-import'
  | 'coa-import';

export type BulkProcessStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

// Helper to convert backend status to frontend status
export const normalizeJobStatus = (
  status: BulkImportJobStatusEnum | string
): BulkProcessStatus => {
  const statusStr = typeof status === 'string' ? status.toUpperCase() : status;
  switch (statusStr) {
    case BulkImportJobStatusEnum.PENDING:
      return 'pending';
    case BulkImportJobStatusEnum.PROCESSING:
      return 'processing';
    case BulkImportJobStatusEnum.COMPLETED:
      return 'completed';
    case BulkImportJobStatusEnum.FAILED:
      return 'failed';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'pending';
  }
};

/**
 * Terminal lifecycle states — a process in one of these is finished and
 * must never be downgraded back to pending/processing by a late/stale
 * status poll. Single source for both the progress-bar status-sync effect
 * and the recovery hook (which prunes terminal jobs from the store).
 */
export const TERMINAL_BULK_STATUSES: BulkProcessStatus[] = [
  'completed',
  'failed',
  'cancelled',
];

export const isTerminalBulkStatus = (status: BulkProcessStatus): boolean =>
  TERMINAL_BULK_STATUSES.includes(status);

export interface BulkProcess {
  id: string;
  type: BulkProcessType;
  status: BulkProcessStatus;
  lastFetchTime: number; // timestamp in milliseconds
  totalItems?: number;
  processedItems?: number;
  failedItems?: number;
  createdAt: number;
  completedAt?: number;
  lastResumedAt?: number; // timestamp when job was last resumed
  error?: string;
}

export type BulkProcessStoreSlice = {
  processes: BulkProcess[];
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
  addProcess: (process: BulkProcess) => void;
  updateProcess: (id: string, updates: Partial<BulkProcess>) => void;
  removeProcess: (id: string) => void;
  getProcessById: (id: string) => BulkProcess | undefined;
  getActiveProcesses: () => BulkProcess[];
  getProcessesByType: (type: BulkProcessType) => BulkProcess[];
  shouldFetch: (id: string, intervalMs?: number) => boolean;
  clearCompletedProcesses: () => void;
  clearAllProcesses: () => void;
  // Recovery methods
  getStaleProcesses: () => BulkProcess[];
  getPendingProcesses: () => BulkProcess[];
  markAsStale: (id: string) => void;
  isProcessStale: (process: BulkProcess) => boolean;
};

export const DefaultBulkProcessStore = {
  processes: [],
  _hasHydrated: false,
};

const FETCH_INTERVAL = 2 * 60 * 1000; // 2 minutes in milliseconds
const STALE_THRESHOLD = 10 * 60 * 1000; // 10 minutes - consider process stale if no update

const bulkProcessStateSlice: StateCreator<
  BulkProcessStoreSlice,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  BulkProcessStoreSlice
> = (set, get) => ({
  ...DefaultBulkProcessStore,
  setHasHydrated: (state: boolean) =>
    set({ _hasHydrated: state }, undefined, 'bulkProcess/setHasHydrated'),

  addProcess: (process: BulkProcess) =>
    set(
      (state) => ({
        processes: [...state.processes, process],
      }),
      undefined,
      'bulkProcess/addProcess'
    ),

  updateProcess: (id: string, updates: Partial<BulkProcess>) =>
    set(
      (state) => ({
        processes: state.processes.map((p) =>
          p.id === id ? { ...p, ...updates, lastFetchTime: Date.now() } : p
        ),
      }),
      undefined,
      'bulkProcess/updateProcess'
    ),

  removeProcess: (id: string) =>
    set(
      (state) => ({
        processes: state.processes.filter((p) => p.id !== id),
      }),
      undefined,
      'bulkProcess/removeProcess'
    ),

  getProcessById: (id: string) => {
    return get().processes.find((p) => p.id === id);
  },

  getActiveProcesses: () => {
    return get().processes.filter(
      (p) => p.status === 'pending' || p.status === 'processing'
    );
  },

  getProcessesByType: (type: BulkProcessType) => {
    return get().processes.filter((p) => p.type === type);
  },

  shouldFetch: (id: string, intervalMs: number = FETCH_INTERVAL) => {
    const process = get().getProcessById(id);
    if (!process) return false;

    // Don't fetch if completed or failed
    if (process.status === 'completed' || process.status === 'failed') {
      return false;
    }

    const now = Date.now();
    const timeSinceLastFetch = now - process.lastFetchTime;
    return timeSinceLastFetch >= intervalMs;
  },

  clearCompletedProcesses: () =>
    set(
      (state) => ({
        processes: state.processes.filter(
          (p) => p.status !== 'completed' && p.status !== 'failed'
        ),
      }),
      undefined,
      'bulkProcess/clearCompletedProcesses'
    ),

  clearAllProcesses: () =>
    set(
      () => DefaultBulkProcessStore,
      undefined,
      'bulkProcess/clearAllProcesses'
    ),

  // Recovery methods
  getStaleProcesses: () => {
    const now = Date.now();
    return get().processes.filter((p) => {
      if (p.status !== 'processing') return false;
      const timeSinceLastUpdate = now - p.lastFetchTime;
      return timeSinceLastUpdate > STALE_THRESHOLD;
    });
  },

  getPendingProcesses: () => {
    return get().processes.filter((p) => p.status === 'pending');
  },

  markAsStale: (id: string) =>
    set(
      (state) => ({
        processes: state.processes.map((p) =>
          p.id === id
            ? {
                ...p,
                error: 'Process appears to be stale',
                status: 'failed' as BulkProcessStatus,
              }
            : p
        ),
      }),
      undefined,
      'bulkProcess/markAsStale'
    ),

  isProcessStale: (process: BulkProcess) => {
    if (process.status !== 'processing') return false;

    const now = Date.now();
    const timeSinceLastUpdate = now - process.lastFetchTime;

    // Only check if no updates for more than 10 minutes
    // Let the backend's isStuck flag handle stuck detection
    return timeSinceLastUpdate > STALE_THRESHOLD;
  },
});

export const useBulkProcessStore = create<BulkProcessStoreSlice>()(
  devtools(
    persist(bulkProcessStateSlice, {
      name: 'bulk-process',
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('[Store] Rehydration error:', error);
          }
          if (state) {
            state.setHasHydrated(true);
          }
        };
      },
    }),
    {
      name: 'bulk-process-store',
    }
  )
);

// Ensure hydration is marked after a delay as fallback
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  setTimeout(() => {
    const state = useBulkProcessStore.getState();
    if (!state._hasHydrated) {
      state.setHasHydrated(true);
    }
  }, 100);
}
